/// <amd-module name="Wit/WorkItemModel/WorkItem/Project" />
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { TreeNodeStructureType, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IProjectDataWithNodes, IProjectProcessData } from "Wit/WorkItemDataManager/Contracts";
import { IProject } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { WorkItemType } from "./WorkItemType";

export class Project implements IProject {
    public name: string;
    public guid: string;
    public workItemTypes: { [name: string]: WorkItemType };
    public workItemTypeNames: string[];
    public fieldIds: number[];
    public extras: any;
    public process: IProjectProcessData | undefined;
    public relatedData: { [key: string]: any };
    public id: number;
    private nodesById: { [key: number]: WorkItemClassificationNode } = {};
    private areaNodesByPath: { [key: string]: WorkItemClassificationNode } = {};
    private iterationNodesByPath: { [key: string]: WorkItemClassificationNode } = {};

    constructor(public projectData: IProjectDataWithNodes) {
        this.id = projectData.id;
        this.name = projectData.name;
        this.guid = projectData.guid;
        this.workItemTypeNames = projectData.workItemTypes;
        this.fieldIds = projectData.fieldIds;
        this.extras = projectData.extras || {};
        this.process = projectData.process;
        this.relatedData = {};

        const rootNode: WorkItemClassificationNode = {
            children: [projectData.areaNodes, projectData.iterationNodes],
            hasChildren: true,
            id: projectData.id,
            name: projectData.name,
            attributes: {},
            path: projectData.name,
            url: "",
            structureType: TreeNodeStructureType.Area,
            identifier: projectData.guid,
            _links: {}
        };

        this.addNodeTreeToCache(rootNode, 0, "");

        if (Array.isArray(this.workItemTypeNames)) {
            this.workItemTypeNames.sort(localeIgnoreCaseComparer);
        }

        this.workItemTypes = {};

        // Event_Service.getService().attachEvent(CSSNodesInvalidated, this._onCSSNodesInvalidated);
    }

    public getNodeById(id: number): WorkItemClassificationNode | undefined {
        if (id in this.nodesById) {
            return this.nodesById[id];
        } else {
            return undefined;
        }
    }

    public getNode(path: string, type: TreeNodeStructureType): WorkItemClassificationNode | undefined {
        let map: { [key: string]: WorkItemClassificationNode } | undefined = undefined;
        if (type === TreeNodeStructureType.Area) {
            map = this.areaNodesByPath;
        } else {
            map = this.iterationNodesByPath;
        }

        if (path in map) {
            return map[path];
        } else {
            return undefined;
        }
    }

    private addNodeTreeToCache(node: WorkItemClassificationNode, depth: number, parentPath: string): void {
        let thisPath: string | undefined = undefined;
        if (depth > 1) {
            thisPath = parentPath + "\\" + node.name;
        } else if (depth === 1) {
            thisPath = parentPath;
        } else {
            thisPath = node.name;
        }

        this.addSingleNodeToCache(node, depth, thisPath);

        if (node.children) {
            node.children.forEach(c => {
                this.addNodeTreeToCache(c, depth + 1, thisPath!);
            });
        }
    }

    private addSingleNodeToCache(node: WorkItemClassificationNode, depth: number, thisPath: string): void {
        const fixedNode: WorkItemClassificationNode = {
            ...node
        };
        fixedNode.path = thisPath;

        this.nodesById[node.id] = fixedNode;

        // The two nodes immediately under project are not directly used for string mapping, the root node is used instead
        if (depth === 1) {
            return;
        }

        // Add to only one or the other, unless structuretype is zero, then add to both
        if (node.structureType === TreeNodeStructureType.Area || depth === 0) {
            this.areaNodesByPath[thisPath] = fixedNode;
        }
        if (node.structureType === TreeNodeStructureType.Iteration || depth === 0) {
            this.iterationNodesByPath[thisPath] = fixedNode;
        }
    }
}
